// src/api/healthcare/AppointmentSettingsApi.ts
/**
 * @file Company Appointment Settings API functions
 * @module api/appointmentSettingsApi
 */

import { ENV } from '../../../config/runtimeEnv';
import { CompanyAppointmentSettings, AppointmentTypeCreateRequest, AppointmentTypeUpdateRequest, TandemRule, TandemRuleCreateRequest } from "../../../types/appointments/AppointmentSettingsTypes";
import { AppointmentType } from "../../../types/appointments/AppointmentTypes";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

/**
 * Fetch the clinic's appointment settings.
 *
 * `company` is gone from all three of these calls (S2): the settings row is a singleton — one
 * database per clinic — and the backend resolves it from the token, ignoring any `company` a
 * stale client still sends.
 */
export const fetchAppointmentSettings = async (): Promise<CompanyAppointmentSettings> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointment-settings`);
	return response.data.data;
};

/**
 * Update the clinic's appointment settings
 */
export const updateAppointmentSettings = async (
	settings: Partial<CompanyAppointmentSettings>
): Promise<CompanyAppointmentSettings> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/appointment-settings`,
		settings
	);
	return response.data.data;
};

/**
 * Reset the clinic's appointment settings to defaults
 */
export const resetAppointmentSettings = async (): Promise<CompanyAppointmentSettings> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-settings/reset`,
		{}
	);
	return response.data.data;
};

/**
 * Fetch all appointment types
 */
export const fetchAppointmentTypes = async (activeOnly?: boolean): Promise<AppointmentType[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointment-types`, {
		params: { active_only: activeOnly },
	});
	return response.data.data;
};

/**
 * Fetch all appointment types (patient)
 */
export const fetchAppointmentTypesPatientAll = async (activeOnly?: boolean): Promise<AppointmentType[]> => {
	const response = await http.get(`${API_BASE_URL}/patient/appointment-types/all`, {
		params: { active_only: activeOnly },
	});
	return response.data.data;
};

/**
 * Fetch a single appointment type by ID
 */
export const fetchAppointmentType = async (id: number): Promise<AppointmentType> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointment-types/${id}`);
	return response.data.data;
};

/**
 * Create a new appointment type
 */
export const createAppointmentType = async (data: AppointmentTypeCreateRequest): Promise<AppointmentType> => {
	const response = await http.post(`${API_BASE_URL}/practitioner/appointment-types`, data);
	return response.data.data;
};

/**
 * Update an existing appointment type
 */
export const updateAppointmentType = async (
	id: number,
	data: AppointmentTypeUpdateRequest
): Promise<AppointmentType> => {
	const response = await http.put(`${API_BASE_URL}/practitioner/appointment-types/${id}`, data);
	return response.data.data;
};

/**
 * Delete an appointment type
 */
export const deleteAppointmentType = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/practitioner/appointment-types/${id}`);
};

/**
 * Fetch bookable appointment types
 */
export const fetchBookableAppointmentTypes = async (
	practitionerId?: UserUuid
): Promise<AppointmentType[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/appointment-types/bookable/list`, {
		params: { practitioner_id: practitionerId },
	});
	return response.data.data;
};

/**
 * Assign practitioners to an appointment type
 */
export const assignPractitionersToType = async (
	typeId: number,
	practitionerIds: UserUuid[]
): Promise<AppointmentType> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${typeId}/assign-practitioners`,
		{ practitioner_ids: practitionerIds }
	);
	return response.data.data;
};

/**
 * Remove practitioners from an appointment type
 */
export const removePractitionersFromType = async (
	typeId: number,
	practitionerIds: UserUuid[]
): Promise<void> => {
	await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${typeId}/remove-practitioners`,
		{ practitioner_ids: practitionerIds }
	);
};

export const fetchTandemRules = async (appointmentTypeId: number): Promise<TandemRule[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/tandem-rules`
	);
	return response.data.data;
};

export const createTandemRule = async (
	appointmentTypeId: number,
	data: TandemRuleCreateRequest
): Promise<TandemRule> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/tandem-rules`,
		data
	);
	return response.data.data;
};

export const updateTandemRule = async (
	appointmentTypeId: number,
	ruleId: number,
	data: Partial<TandemRuleCreateRequest>
): Promise<TandemRule> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/tandem-rules/${ruleId}`,
		data
	);
	return response.data.data;
};

export const deleteTandemRule = async (appointmentTypeId: number, ruleId: number): Promise<void> => {
	await http.delete(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/tandem-rules/${ruleId}`
	);
};

export const toggleTandemRuleActive = async (appointmentTypeId: number, ruleId: number): Promise<TandemRule> => {
	const response = await http.patch(
		`${API_BASE_URL}/practitioner/appointment-types/${appointmentTypeId}/tandem-rules/${ruleId}/toggle-active`,
		{}
	);
	return response.data.data;
};

/**
 * A notification template offered as an appointment type's confirmation email (S5, ClickUp 86d42yjtf).
 *
 * ⚠️ In the `appointment_scheduled_notification` category `is_active` means "this is the clinic-wide
 * DEFAULT", not "usable" — inactive templates are returned deliberately and are selectable.
 */
export interface ConfirmationTemplate {
	id: number;
	name: string;
	type?: string;
	subject?: string;
	is_active: boolean;
}

export const fetchAvailableConfirmationTemplates = async (): Promise<ConfirmationTemplate[]> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/confirmation-templates`);
	const data = response.data;
	return Array.isArray(data) ? data : (data?.data ?? []);
};
